import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, MagnifyingGlass, Warning, ArrowClockwise, Briefcase } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const PUEBLE_CATEGORIES = ["Operativos", "Mantenimiento", "Auxiliares", "Almacén", "Otros"];
const CATEGORY_STYLE = {
  Operativos: "bg-blue-100 text-blue-900 border-blue-300",
  Mantenimiento: "bg-orange-100 text-orange-900 border-orange-300",
  Auxiliares: "bg-purple-100 text-purple-900 border-purple-300",
  "Almacén": "bg-emerald-100 text-emerald-900 border-emerald-300",
  Otros: "bg-slate-100 text-slate-700 border-slate-300",
};

/**
 * Catálogo de puestos → rol Pueble.
 *
 * Cada fila mapea un alias (substring case-insensitive del puesto en Nómina)
 * al rol Pueble que debe aparecer en el reporte. El botón "Importar
 * faltantes" trae los puestos que aparecieron como Sin Clasificar la última
 * vez que se corrió el reporte y sugiere agregarlos.
 */
export default function PositionsCatalogPanel() {
  const [rows, setRows] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);   // {id?, alias, pueble_role, category, active, notes}
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/hr/positions").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/hr/positions/unmatched").then((r) => setUnmatched(r.data.unmatched || [])).catch(() => setUnmatched([]));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const ql = q.toLowerCase();
    return rows.filter((r) =>
      r.alias.toLowerCase().includes(ql) || r.pueble_role.toLowerCase().includes(ql));
  }, [rows, q]);

  const byRole = useMemo(() => {
    const acc = {};
    for (const r of rows) {
      if (!r.active) continue;
      acc[r.pueble_role] = (acc[r.pueble_role] || 0) + 1;
    }
    return acc;
  }, [rows]);

  const openNew = (prefill = null) => setEditing({
    alias: prefill?.alias || "",
    pueble_role: prefill?.pueble_role || "",
    category: prefill?.category || "",
    active: true, notes: "",
  });
  const openEdit = (r) => setEditing({ ...r, category: r.category || "" });

  const save = async () => {
    if (!editing?.alias?.trim() || !editing?.pueble_role?.trim()) {
      toast.error("Alias y rol Pueble son obligatorios");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/hr/positions/${editing.id}`, editing);
        toast.success("Puesto actualizado");
      } else {
        await api.post("/hr/positions", editing);
        toast.success("Puesto agregado al catálogo");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este alias del catálogo? El rol dejará de matchear.")) return;
    try { await api.delete(`/hr/positions/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (r) => {
    try {
      await api.put(`/hr/positions/${r.id}`, { ...r, active: !r.active });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="positions-catalog-panel">
      <div>
        <h3 className="font-heading font-black text-xl text-slate-900 flex items-center gap-2">
          <Briefcase size={22} weight="fill" className="text-blue-900" /> Puestos y Roles Pueble
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          Homologación entre puestos capturados en Nómina y roles que aparecen en el reporte
          Pueble. El alias hace match case-insensitive por substring — un mismo rol puede
          tener varios alias (ej. &ldquo;operador jumbo&rdquo; y &ldquo;op. jumbo lineal&rdquo; &rarr; Op. Jumbo lineal).
        </p>
      </div>

      {/* Unmatched banner */}
      {unmatched.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="positions-unmatched-banner">
          <p className="text-sm font-bold text-amber-900 flex items-center gap-2 mb-2">
            <Warning size={16} weight="fill" /> {unmatched.length} puesto(s) sin homologar
          </p>
          <p className="text-xs text-amber-800 mb-2">
            Aparecen en Nómina pero no matchean con ningún alias del catálogo — irán a &ldquo;Sin clasificar&rdquo;
            en el Pueble. Haz click para agregarlos.
          </p>
          <div className="flex flex-wrap gap-1">
            {unmatched.slice(0, 20).map((u, i) => (
              <button
                key={i}
                onClick={() => openNew({ alias: u.position.toLowerCase(), pueble_role: u.position })}
                data-testid={`positions-unmatched-${i}`}
                className="text-[11px] px-2 py-1 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 font-semibold gap-1"
                title={`${u.count} colaborador(es)`}
              >
                {u.position} <span className="opacity-60">×{u.count}</span> +
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters + actions */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="overline-label">Buscar</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-7 h-9" value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Alias o rol Pueble…" data-testid="positions-search" />
          </div>
        </div>
        <Button variant="outline" onClick={load} data-testid="positions-refresh" className="gap-1.5">
          <ArrowClockwise size={14} /> Refrescar
        </Button>
        <Button onClick={() => openNew()} data-testid="positions-new" className="bg-blue-900 hover:bg-blue-800 gap-1.5">
          <Plus size={14} weight="bold" /> Nuevo alias
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="positions-table">
          <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Rol Pueble</th>
              <th className="text-left px-3 py-2">Categoría</th>
              <th className="text-left px-3 py-2">Alias (Nómina)</th>
              <th className="text-center px-3 py-2">Activo</th>
              <th className="text-left px-3 py-2">Notas</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400 italic">Sin resultados</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`positions-row-${r.id}`} className={r.active ? "" : "opacity-50"}>
                <td className="px-3 py-2 font-semibold text-slate-900">
                  {r.pueble_role}
                  <span className="ml-2 text-[10px] text-slate-400">×{byRole[r.pueble_role] || 0}</span>
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${CATEGORY_STYLE[r.category] || CATEGORY_STYLE.Otros}`}
                         data-testid={`positions-cat-${r.id}`}>
                    {r.category || "Otros"}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-700">{r.alias}</td>
                <td className="text-center px-3 py-2">
                  <Switch checked={r.active} onCheckedChange={() => toggleActive(r)}
                           data-testid={`positions-toggle-${r.id}`} />
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 max-w-xs truncate">{r.notes || ""}</td>
                <td className="text-right px-3 py-2 whitespace-nowrap">
                  <button onClick={() => openEdit(r)} className="text-blue-700 hover:underline text-xs mr-2"
                          data-testid={`positions-edit-${r.id}`}>
                    <PencilSimple size={14} className="inline" /> Editar
                  </button>
                  <button onClick={() => del(r.id)} className="text-red-500 hover:text-red-700 p-1"
                          data-testid={`positions-del-${r.id}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-500 italic">
        <b>Tip:</b> el alias no distingue mayúsculas/minúsculas y matchea como substring —
        &ldquo;jumbo&rdquo; matcheará &ldquo;Operador Jumbo&rdquo; y &ldquo;OPERADOR DE JUMBO ANCLADOR&rdquo;. El alias más
        específico gana sobre el más corto en caso de conflicto.
      </p>

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="bg-white max-w-md" data-testid="positions-dialog"
                        aria-describedby="positions-dlg-desc">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editar alias" : "Nuevo alias de puesto"}</DialogTitle>
            <p id="positions-dlg-desc" className="text-xs text-slate-500">
              El alias es texto que aparece en el puesto de Nómina; el Rol Pueble es el
              nombre que se muestra en el reporte.
            </p>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Alias (buscar en Nómina)</Label>
                <Input value={editing.alias}
                        onChange={(e) => setEditing({ ...editing, alias: e.target.value.toLowerCase() })}
                        placeholder="ej. operador jumbo"
                        data-testid="positions-alias-input" />
              </div>
              <div>
                <Label>Rol Pueble (nombre en reporte)</Label>
                <Input value={editing.pueble_role}
                        onChange={(e) => setEditing({ ...editing, pueble_role: e.target.value })}
                        placeholder="ej. Op. Jumbo lineal"
                        data-testid="positions-role-input" />
              </div>
              <div>
                <Label>Categoría <span className="text-slate-400 text-[10px]">(auto si vacío)</span></Label>
                <Select value={editing.category || "__auto"}
                        onValueChange={(v) => setEditing({ ...editing, category: v === "__auto" ? "" : v })}>
                  <SelectTrigger data-testid="positions-category-input">
                    <SelectValue placeholder="Auto-clasificar" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="__auto">Auto-clasificar por rol</SelectItem>
                    {PUEBLE_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Input value={editing.notes || ""}
                        onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })}
                         data-testid="positions-active-toggle" />
                <span className="text-sm">Activo</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800"
                     data-testid="positions-save">
              {saving ? "…" : (editing?.id ? "Guardar" : "Agregar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
